import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { clearStorageScopeUser, migrateLegacyCachesForUser } from '../utils/storageScope';
import { prefetchWorkbenchStates } from '../utils/workbenchSync';
import { inferProductFromPath } from '../utils/productRouting';
import { navigateOrRedirect } from '../utils/desktopNavigation';
import { getApiBaseUrl, rememberApiBaseUrl } from '../utils/apiBase';
import { safeSetItem } from '../utils/safeStorage';
import { mergeUserPreservingOnboarding } from '../utils/signupQuestions';
import { resetUpgradeNudgeSession } from '../utils/upgradeNudgeStorage';
import { clearFirstSignupHome, markFirstSignupHome } from '../utils/signupLanding';
import { isLocalDevPreview, LOCAL_PREVIEW_USER } from '../utils/localDevPreview';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const consumeInFlightRef = useRef(false);
  const initializeInFlightRef = useRef(false);

  // Warm every module's workbench localStorage cache right after sign-in so
  // sidebars restore their last state instantly on mount instead of showing
  // defaults until the per-module server fetch lands. Runs after the storage
  // scope snapshot restore (which happens synchronously during auth).
  useEffect(() => {
    if (!user?.id) return;
    prefetchWorkbenchStates(user.id).catch(() => {
      // Best-effort warm-up; modules still hydrate from the server themselves.
    });
  }, [user?.id]);

  const clearDemoAuthArtifacts = () => {
    // Prevent stale demo flags/tokens from bypassing protected routes.
    localStorage.removeItem('demo_mode');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
  };

  const getStoredAuthToken = () =>
    localStorage.getItem('token') || localStorage.getItem('auth_token') || '';

  const setStoredAuthToken = (token) => {
    if (!token) return;
    localStorage.setItem('token', token);
    localStorage.setItem('auth_token', token);
  };

  const clearStoredAuthToken = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
  };

  const activateUserStorageScope = (userId) => {
    if (!userId) return;
    // Defense-in-depth: storageScope should never throw, but a thrown
    // QuotaExceededError or any other storage failure here would be
    // caught by the auth try/catch and observed by the user as a hard
    // sign-in bounce (May 27 2026 regression). Wrap defensively so
    // auth always continues even if scoped-storage migration fails.
    try {
      migrateLegacyCachesForUser(userId);
    } catch (e) {
      try {
        // eslint-disable-next-line no-console
        console.warn('[AuthContext] activateUserStorageScope swallowed storage error', e);
      } catch { /* ignore */ }
    }
  };

  const persistApiBaseForSession = () => {
    try {
      const existing = localStorage.getItem('xph_api_base_url');
      if (existing && /^https?:\/\//i.test(existing)) {
        return;
      }
      rememberApiBaseUrl(getApiBaseUrl());
    } catch {
      // Ignore storage errors.
    }
  };

  const applyDashboardThemeFromUser = (userData) => {
    try {
      const persistedTheme = userData?.settings?.ui?.dashboardTheme === 'light' ? 'light' : 'dark';
      localStorage.setItem('xph_dashboard_theme', persistedTheme);
      window.dispatchEvent(new Event('xph-dashboard-theme-change'));
    } catch {
      // Ignore storage errors.
    }
  };

  const parseAuthPayloadFromQuery = (authParam) => {
    if (!authParam) return null;
    const normalized = String(authParam).trim();
    const candidates = [
      normalized,
      normalized.replace(/ /g, '+'),
      normalized.replace(/\+/g, '%2B'),
      normalized.replace(/\+/g, '%20'),
    ];

    for (const candidate of candidates) {
      // JSON payload (preferred)
      try {
        return JSON.parse(candidate);
      } catch (_) {
        try {
          return JSON.parse(decodeURIComponent(candidate));
        } catch {
          // Try next candidate.
        }
      }

      // Base64-encoded JSON payload fallback (legacy/edge providers)
      try {
        const base64 = candidate.replace(/-/g, '+').replace(/_/g, '/');
        const pad = '='.repeat((4 - (base64.length % 4)) % 4);
        const decoded = atob(base64 + pad);
        const parsed = JSON.parse(decoded);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch {
        // Try next candidate.
      }
    }

    // Token-only fallback (JWT or opaque token): still allows auth hydration.
    if (/^[A-Za-z0-9\-_=]+\.[A-Za-z0-9\-_=]+\.[A-Za-z0-9\-_=]+$/.test(normalized)) {
      return { token: normalized };
    }

    return null;
  };

  const getRawQueryParam = (name) => {
    const search = window.location.search || '';
    const match = search.match(new RegExp(`[?&]${name}=([^&]*)`));
    return match ? match[1] : null;
  };

  const removeAuthParamsFromCurrentUrl = () => {
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('auth');
    nextUrl.searchParams.delete('returnTo');
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  };

  const consumeAuthPayloadFromCurrentUrl = () => {
    if (consumeInFlightRef.current) {
      return false;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const authParam = urlParams.get('auth');
    const rawAuthParam = getRawQueryParam('auth');
    const returnToParam = urlParams.get('returnTo');
    if (!authParam && !rawAuthParam) return false;

    consumeInFlightRef.current = true;
    try {
      const authData = parseAuthPayloadFromQuery(authParam || rawAuthParam);
      if (!authData) throw new Error('Invalid auth payload');
      const authToken = authData.token || authData.accessToken || authData.jwt || null;
      const authUser = authData.user || authData.profile || authData.client || null;
      const authProduct = authData.product || authData.loginProduct || null;

      clearDemoAuthArtifacts();
      if (authToken) {
        setStoredAuthToken(authToken);
        persistApiBaseForSession();
      }
      if (authUser) {
        activateUserStorageScope(authUser.id);
        localStorage.setItem('user', JSON.stringify(authUser));
        applyDashboardThemeFromUser(authUser);
      } else {
        // Token-only handoff (the marketing site sends `?auth={token,product}`
        // to keep the URL short and avoid 414 Request-URI Too Large). The
        // fresh JWT may resolve to a DIFFERENT identity than the one
        // currently in `localStorage.user` — e.g. a visitor who previously
        // signed into the Partner Portal still has the partner user cached
        // when they sign back in as a customer. If we leave that stale
        // user in place, `initializeAuth()` will optimistically render it
        // for "instant auth", `ProtectedRoute` will see
        // `user.accountType === 'partner'` and immediately bounce to
        // /partner-portal, all BEFORE the background `/auth/me` call
        // returns the correct fresh identity (May 27 2026 regression
        // surfaced by the Partner Portal -> Intelligence Navigator
        // sign-in flow). Wiping the stale identity here forces
        // initializeAuth() into the synchronous validate-and-hydrate
        // path, so the first frame the user sees uses the JWT's actual
        // identity. We do NOT touch the token (we just stored a fresh
        // one) or product (preserved if present), only identity-scoped
        // caches that could be mismatched.
        try {
          localStorage.removeItem('user');
          clearStorageScopeUser();
        } catch {
          // best-effort
        }
      }
      if (authProduct) {
        localStorage.setItem('currentProduct', authProduct);
      }
      if (authUser?.companyProfile?.completedAt) {
        localStorage.setItem(`hasSeenIntegrationHub_${authUser.id}`, 'true');
      }

      const newUserFlag = localStorage.getItem('newUser');
      if (newUserFlag === 'true') {
        markFirstSignupHome(authUser?.id || authUser?.email);
      }

      if (authUser) {
        setUser(authUser);
        setLoading(false);
      } else {
        // Token-only payload: validate and hydrate user via /auth/me.
        // Stale `user` cache was cleared just above, so initializeAuth
        // will take the synchronous "no stored user" branch instead of
        // optimistically rendering the previous account.
        initializeAuth();
      }

      removeAuthParamsFromCurrentUrl();
      const cleanUrl = window.location.pathname;

      if (returnToParam && returnToParam.startsWith('/') && returnToParam !== cleanUrl) {
        navigateOrRedirect(returnToParam);
        return true;
      }

      const payloadReturnPath = String(authData.returnPath || authData.returnTo || '').trim();
      if (payloadReturnPath && payloadReturnPath.startsWith('/') && payloadReturnPath !== cleanUrl) {
        navigateOrRedirect(payloadReturnPath);
      }

      return true;
    } catch (e) {
      // Never leave a broken auth payload in the URL; it can cause
      // ProtectedRoute to wait indefinitely for auth hydration.
      try {
        removeAuthParamsFromCurrentUrl();
      } catch {
        // Ignore history replacement errors.
      }
      setLoading(false);
      if (getStoredAuthToken()) {
        initializeAuth();
      }
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Failed to parse auth data from URL:', e);
      }
      return false;
    } finally {
      consumeInFlightRef.current = false;
    }
  };

  useEffect(() => {
    const handled = consumeAuthPayloadFromCurrentUrl();
    if (!handled) {
      initializeAuth();
    }

    const handleRouteSignal = () => {
      if (consumeInFlightRef.current) {
        return;
      }
      const didConsume = consumeAuthPayloadFromCurrentUrl();
      if (!didConsume) {
        const token = getStoredAuthToken();
        if (token && !initializeInFlightRef.current) {
          initializeAuth();
        }
      }
    };

    window.addEventListener('popstate', handleRouteSignal);

    return () => {
      window.removeEventListener('popstate', handleRouteSignal);
    };
  }, []);

  const enterLocalPreview = () => {
    if (!isLocalDevPreview() || getStoredAuthToken()) return false;
    setUser(LOCAL_PREVIEW_USER);
    return true;
  };

  const initializeAuth = async () => {
    if (initializeInFlightRef.current) {
      return;
    }
    initializeInFlightRef.current = true;
    try {
      if (enterLocalPreview()) return;
      const token = getStoredAuthToken();
      const storedUser = localStorage.getItem('user');
      console.log('🔐 AuthContext: Initializing auth, token:', token ? 'Present' : 'Missing', 'storedUser:', storedUser ? 'Present' : 'Missing');
      
      if (token) {
        // Keep API origin aligned with the active authenticated desktop session.
        persistApiBaseForSession();
        // First, check if we have stored user data (from Next.js landing page login)
        // This allows immediate authentication without waiting for backend validation
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            console.log('📦 Using stored user data immediately:', userData);
            activateUserStorageScope(userData?.id);
            applyDashboardThemeFromUser(userData);
            setUser(userData);
            
            // Validate token in background - if invalid, clear everything
            authService.validateToken(token)
              .then(validatedUser => {
                console.log('✅ Token validated in background, updating user:', validatedUser);
                clearDemoAuthArtifacts();
                activateUserStorageScope(validatedUser?.id);
                applyDashboardThemeFromUser(validatedUser);
                setUser((prev) => {
                  const next = mergeUserPreservingOnboarding(prev, validatedUser);
                  try {
                    localStorage.setItem('user', JSON.stringify(next));
                  } catch (storageError) {
                    console.warn('[AuthContext] Failed to persist user to localStorage; in-memory state retained', storageError);
                  }
                  return next;
                });
              })
              .catch(err => {
                const statusCode = Number(err?.statusCode || 0);
                const shouldClearAuth = statusCode === 401 || statusCode === 403;
                if (shouldClearAuth) {
                  console.error('❌ Token rejected, clearing auth data:', err.message);
                  clearStoredAuthToken();
                  localStorage.removeItem('user');
                  clearStorageScopeUser();
                  if (!enterLocalPreview()) setUser(null);
                } else if (process.env.NODE_ENV !== 'production') {
                  console.warn('⚠️ Token validation transient failure; preserving local auth state:', err.message);
                }
              });
            
            return; // Exit early, loading will be set to false in finally
          } catch (parseError) {
            console.error('❌ Failed to parse stored user:', parseError);
          }
        }
        
        // No stored user - validate token synchronously
        try {
          const userData = await authService.validateToken(token);
          console.log('✅ Token validated, user:', userData);
          clearDemoAuthArtifacts();
          activateUserStorageScope(userData?.id);
          applyDashboardThemeFromUser(userData);
          setUser(userData);
          // Quota-safe: evicts regenerable caches when localStorage is full.
          // React state is already set above, so a failed write is non-fatal.
          safeSetItem('user', JSON.stringify(userData));
        } catch (error) {
          const statusCode = Number(error?.statusCode || 0);
          const shouldClearAuth = statusCode === 401 || statusCode === 403;
          console.error('❌ Token validation failed:', error);
          if (shouldClearAuth) {
            console.log('🗑️ Clearing invalid token');
            clearStoredAuthToken();
            localStorage.removeItem('user');
            clearStorageScopeUser();
            enterLocalPreview();
          } else if (process.env.NODE_ENV !== 'production') {
            console.warn('⚠️ Preserving desktop auth session after validation failure:', error?.message);
          }
        }
      }
    } catch (error) {
      console.error('🔥 Error initializing auth:', error);
      clearStoredAuthToken();
      localStorage.removeItem('user');
      clearStorageScopeUser();
      enterLocalPreview();
    } finally {
      setLoading(false);
      initializeInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (loading || user) return;
    const token = getStoredAuthToken();
    if (!token) return;
    initializeAuth();
  }, [loading, user]);

  // API layers dispatch `xph-auth-expired` when the server 401s a request
  // (see utils/apiBase.js). Tokens expire after 7 days, so a long-lived tab
  // can go stale mid-session: without this, the app just sits on a
  // working-looking UI while every call silently fails. Re-validate the
  // token once and, if it's genuinely dead, clear the session so
  // ProtectedRoute bounces to sign-in.
  const authExpiredCheckRef = useRef(0);
  useEffect(() => {
    const onAuthExpired = () => {
      const token = getStoredAuthToken();
      if (!token) return;
      const now = Date.now();
      if (now - authExpiredCheckRef.current < 15000) return;
      authExpiredCheckRef.current = now;
      authService.validateToken(token)
        .then((validatedUser) => {
          // Token still good — the 401 was endpoint-specific; leave session alone.
          if (validatedUser) {
            setUser((prev) => mergeUserPreservingOnboarding(prev, validatedUser));
          }
        })
        .catch((err) => {
          const statusCode = Number(err?.statusCode || 0);
          if (statusCode === 401 || statusCode === 403) {
            console.error('❌ Session expired — clearing auth and returning to sign-in');
            clearStoredAuthToken();
            localStorage.removeItem('user');
            clearStorageScopeUser();
            setUser(null);
          }
        });
    };
    window.addEventListener('xph-auth-expired', onAuthExpired);
    return () => window.removeEventListener('xph-auth-expired', onAuthExpired);
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.login(email, password);
      console.log('Login response:', response);
      
      // Check if 2FA is required
      if (response.requires2FA) {
        setLoading(false);
        return { 
          success: true, 
          requires2FA: true, 
          tempUserId: response.tempUserId 
        };
      }
      
      setStoredAuthToken(response.token);
      persistApiBaseForSession();
      console.log('Token stored:', response.token);
      clearDemoAuthArtifacts();
      activateUserStorageScope(response.user?.id);
      
      // Check if user logged in from a product landing page
      const currentProduct = localStorage.getItem('currentProduct');
      const requestedProduct = ['autopilot', 'data-iq', 'knowledge-iq'].includes(currentProduct)
        ? currentProduct
        : null;
      const hasExistingProductAccess = Boolean(
        requestedProduct && response.user?.productAccess?.includes(requestedProduct)
      );
      const sessionUser = response.user
        ? {
            ...response.user,
            productAccess: requestedProduct && !hasExistingProductAccess
              ? [...(Array.isArray(response.user.productAccess) ? response.user.productAccess : []), requestedProduct]
              : response.user.productAccess,
          }
        : response.user;
      console.log('🎯 AuthContext login - currentProduct from localStorage:', currentProduct);
      console.log('🎯 AuthContext login - user productAccess:', response.user?.productAccess);

      // Hydrate auth state immediately so protected routes don't wait for a second auth pass.
      if (sessionUser) {
        applyDashboardThemeFromUser(sessionUser);
        setUser(sessionUser);
        localStorage.setItem('user', JSON.stringify(sessionUser));
      }
      
      // ALWAYS add product access if logging in from a product landing page
      // This ensures the user can access the product they're trying to use
      if (requestedProduct && response.user) {
        const hasAccess = response.user.productAccess?.includes(requestedProduct);
        if (!hasAccess) {
          console.log(`🔄 Adding ${currentProduct} access for user...`);
          fetch('https://api.xpherium.com/api/auth/add-product', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${response.token}`
            },
            body: JSON.stringify({ product: requestedProduct })
          })
            .then(async (addProductResponse) => {
              if (!addProductResponse.ok) {
                console.error('Failed to add product access - response not ok');
                return;
              }
              const result = await addProductResponse.json().catch(() => ({}));
              console.log(`✅ ${requestedProduct} access added:`, result);
            })
            .catch((addError) => {
              console.error('Failed to add product access:', addError);
            });
        }
        
        // ALWAYS redirect to the product the user logged in from
        // This is the key fix - respect the landing page context
        let redirectUrl = '/dashboard';
        if (requestedProduct === 'autopilot') {
          redirectUrl = '/app/autopilot';
        } else if (requestedProduct === 'data-iq') {
          redirectUrl = '/app/data-iq';
        } else if (requestedProduct === 'knowledge-iq') {
          redirectUrl = '/app/knowledge-iq';
        }
        
        console.log(`🚀 Redirecting to ${redirectUrl} (based on currentProduct: ${currentProduct})`);
        navigateOrRedirect(redirectUrl);
        return { success: true, redirectUrl, redirected: true };
      }
      
      // No product context - user state is already hydrated above.
      console.log('User set:', sessionUser);
      
      return { success: true, redirectUrl: '/dashboard' };
    } catch (error) {
      console.log('Login error:', error);
      setError(error.message);
      return { 
        success: false, 
        error: error.message,
        failedAttempts: error.failedAttempts,
        suggestPasswordReset: error.suggestPasswordReset,
        accountLocked: error.accountLocked
      };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔐 AuthContext: Starting signup...', userData);
      
      const response = await authService.signup(userData);
      console.log('🔐 AuthContext: Signup response received:', response);
      
      // Store token and user data temporarily, but don't authenticate yet
      // Authentication happens after email/phone verification
      localStorage.setItem('pendingUser', JSON.stringify({
        token: response.token,
        user: response.user
      }));
      console.log('🔐 AuthContext: Stored pendingUser in localStorage');
      
      // Mark as new user for onboarding flow
      localStorage.setItem('newUser', 'true');
      markFirstSignupHome(response.user?.id || response.user?.email); // Ensure first-time users land on home page
      
      console.log('🔐 AuthContext: Returning success=true');
      return { success: true, isNewUser: true, user: response.user };
    } catch (error) {
      console.error('🔐 AuthContext: Signup error:', error);
      
      // Handle EMAIL_EXISTS case specially
      if (error.code === 'EMAIL_EXISTS') {
        setError(error.message);
        return { 
          success: false, 
          error: error.message,
          code: 'EMAIL_EXISTS',
          hasProductAccess: error.hasProductAccess,
          requestedProduct: error.requestedProduct
        };
      }
      
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // New method to complete verification and authenticate user
  const completeVerification = () => {
    try {
      const pendingUser = localStorage.getItem('pendingUser');
      if (pendingUser) {
        const { token, user: userData } = JSON.parse(pendingUser);
        setStoredAuthToken(token);
        persistApiBaseForSession();
        activateUserStorageScope(userData?.id);
        applyDashboardThemeFromUser(userData);
        setUser(userData);
        localStorage.removeItem('pendingUser');
        return { success: true };
      }
      return { success: false, error: 'No pending user found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Merge a partial update into the current user and persist it. Used by
  // self-service profile edits (e.g. avatar upload) so the UI reflects the
  // change immediately without waiting for a full re-auth.
  const updateUser = (partial) => {
    if (!partial || typeof partial !== 'object') return;
    setUser((prev) => {
      const next = { ...(prev || {}), ...partial };
      safeSetItem('user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    // Preserve product context so post-logout redirects stay product-specific.
    const path = window.location.pathname;
    const inferredProduct = inferProductFromPath(path);
    localStorage.setItem('currentProduct', inferredProduct);

    // Clear auth data
    if (user?.id) resetUpgradeNudgeSession(user.id);
    try { sessionStorage.removeItem('xph-welcome-offer-banner-dismissed'); } catch { /* ignore */ }
    clearStoredAuthToken();
    localStorage.removeItem('user');
    localStorage.removeItem('pendingUser');
    localStorage.removeItem('newUser');
    clearFirstSignupHome();
    localStorage.removeItem('companyProfile');
    localStorage.removeItem('pendingInviteToken');
    clearDemoAuthArtifacts();
    clearStorageScopeUser();
    
    if (!enterLocalPreview()) setUser(null);
    setError(null);
  };

  // Debug log before creating value object
  console.log('AuthContext render - user:', user, 'isAuthenticated:', !!user, 'loading:', loading);

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    completeVerification,
    updateUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};